// =============================================================================
// PickersPage.jsx
// =============================================================================
// Picker task queue — task list for staff in "picker" mode.
// Shows device / stationery_qld tasks with filters, take-on, release, and
// completion.
//
// Props:
//   onViewRequest  — fn(requestId, refNo, soNumber) — navigate to a request, scoped
//                    to the task's Sales Order (so the picker view shows just that SO)
// Context:
//   user           — current user object, from useSession
//   requests       — cached request list (used to filter out draft-linked tasks), from useRequests
//
// Extracted from DashboardPages.jsx (May 2026).
// =============================================================================

import React from "react";
import { isAdminRole } from "../../app/access";
import { useRequests } from "../../app/RequestsContext.jsx";
import { useSession } from "../../app/SessionContext.jsx";
import {
  listPickerTasks,
  PICKER_TASK_TYPES,
  updateTaskByIdReturning,
} from "../../data/requestTasks";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { useFitViewportHeight } from "../../hooks/useFitViewportHeight";
import { useLiveList } from "../../hooks/useLiveList";
import { usePageSize } from "../../hooks/usePageSize";
import { REQUEST_STATUSES } from "../../utils/enums";
import { fmtDateTimeLocal } from "../../utils/helpers";
import { PageNav } from "../common/PageNav.jsx";
import { RequestRefLink } from "../common/RequestRefLink.jsx";

// Rows per page is the shared per-device preference (services/pageSize.ts) — the
// old fixed 25 is gone, John's call 6 Aug: no per-screen exceptions.

// ── Atoms (private to this file) ────────────────────────────────────────────
// top_up is a first-class picker task type (data/requestTasks.ts PICKER_TASK_TYPES) but
// had no label or colour here, so those rows rendered the raw string "top_up".
const PICKER_TYPE_LABELS = {
  device: "Device",
  stationery_qld: "Stationery Pack",
  top_up: "Top-up",
};
var PICKER_TYPE_COLOURS = {
  device: { bg: "#E3F2FD", text: "#0C447C", border: "#90CAF9" },
  stationery_qld: { bg: "#E8F5E9", text: "#1B5E20", border: "#A5D6A7" },
  top_up: { bg: "#FFF3E0", text: "#E65100", border: "#FFCC80" },
};

// ─────────────────────────────────────────────────────────────────────────────
// PickerTaskBadge — colour-coded badge for a picker-only task type
// ─────────────────────────────────────────────────────────────────────────────
function PickerTaskBadge({ type }) {
  var col = PICKER_TYPE_COLOURS[type] || { bg: "#F5F5F5", text: "#607D79", border: "#E0E0E0" };
  var label = PICKER_TYPE_LABELS[type] || type;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        background: col.bg,
        color: col.text,
        border: `1px solid ${col.border}`,
        borderRadius: 5,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PickersPage — picker task queue
// ─────────────────────────────────────────────────────────────────────────────
function PickersPage({ onViewRequest }) {
  const [PICKER_PAGE_SIZE] = usePageSize();
  // `user` + `requests` now come from context rather than props (Phase 3 refactor).
  const { user } = useSession();
  const { requests, loadPickerTasks } = useRequests();
  // Supervisor (admin/superuser) — may take over / release any picker's task;
  // a plain picker is limited to unassigned tasks + their own (see the row gates).
  const isSupervisor = isAdminRole(user);
  // Filters persist across navigation (e.g. opening a task and coming back) via
  // sessionStorage, matching ToDoPage. Browser close clears them.
  function loadFilter(key, fallback) {
    try {
      var v = sessionStorage.getItem(`zephyr_pickers_${key}`);
      return v == null ? fallback : v;
    } catch (_e) {
      return fallback;
    }
  }
  function saveFilter(key, value) {
    try {
      sessionStorage.setItem(`zephyr_pickers_${key}`, value);
    } catch (_e) {}
  }
  // Back-compat: the status-filter tokens were renamed (pending -> unactioned,
  // completed -> actioned, 2026-06-27) so the task-queue's "done"/"not done"
  // vocabulary stops colliding with the item-approval status "Pending" and the
  // (reserved) future Request status "Completed". Map any value a returning user
  // saved under the old names so their persisted filter still resolves.
  function normaliseStatusFilter(v) {
    if (v === "pending") return "unactioned";
    if (v === "completed") return "actioned";
    return v;
  }
  // The task LIST is now live (C3): Realtime on request_tasks with delta-apply, plus the
  // hook's own fallback poll + reconnect reconcile. `tasks` and `loading` come from it.
  const {
    rows: tasks,
    loading,
    refresh,
    applyChange,
  } = useLiveList({
    table: "request_tasks",
    fetch: listPickerTasks,
    rowBelongs: function (t) {
      return PICKER_TASK_TYPES.includes(t.type);
    },
    sort: function (a, b) {
      // Oldest first — the Pickers queue is worked oldest-first (matches listPickerTasks).
      return (a.created_at || "").localeCompare(b.created_at || "");
    },
  });
  // Filter state initialises from sessionStorage; wrapped setters mirror back to it
  // so the filters survive opening a task and returning (the screen remounts).
  var _filterStatus = React.useState(function () {
    return normaliseStatusFilter(loadFilter("status", "unactioned"));
  });
  var filterStatus = _filterStatus[0];
  var setFilterStatusRaw = _filterStatus[1];
  function setFilterStatus(v) {
    saveFilter("status", v);
    setFilterStatusRaw(v);
  }
  var _filterType = React.useState(function () {
    return loadFilter("type", "all");
  });
  var filterType = _filterType[0];
  var setFilterTypeRaw = _filterType[1];
  function setFilterType(v) {
    saveFilter("type", v);
    setFilterTypeRaw(v);
  }
  var _filterState = React.useState(function () {
    return loadFilter("state", "all");
  });
  var filterState = _filterState[0];
  var setFilterStateRaw = _filterState[1];
  function setFilterState(v) {
    saveFilter("state", v);
    setFilterStateRaw(v);
  }
  var _filterStaff = React.useState(function () {
    return loadFilter("staff", "all");
  });
  var filterStaff = _filterStaff[0];
  var setFilterStaffRaw = _filterStaff[1];
  function setFilterStaff(v) {
    saveFilter("staff", v);
    setFilterStaffRaw(v);
  }
  var _filterShelter = React.useState(function () {
    return loadFilter("shelter", "all");
  });
  var filterShelter = _filterShelter[0];
  var setFilterShelterRaw = _filterShelter[1];
  function setFilterShelter(v) {
    saveFilter("shelter", v);
    setFilterShelterRaw(v);
  }
  var _search = React.useState(function () {
    return loadFilter("search", "");
  });
  var search = _search[0];
  var setSearchRaw = _search[1];
  function setSearch(v) {
    saveFilter("search", v);
    setSearchRaw(v);
  }
  const [page, setPage] = React.useState(0);

  // Slim badge poll (C3, pragmatic): the LIST is live via useLiveList; this poll exists
  // ONLY to keep the nav badge count fresh (A2 behaviour). Deduped in usePortalData;
  // guarded for test providers that don't supply the loader.
  useAutoRefresh(function () {
    if (loadPickerTasks) loadPickerTasks();
  });

  async function takeOn(task) {
    var now = new Date().toISOString();
    var { data, error } = await updateTaskByIdReturning(task.id, {
      assigned_to: user.shelterName,
      assigned_to_id: user.id,
      assigned_at: now,
    });
    if (!error && data) {
      // Optimistic patch via the same delta path a Realtime UPDATE uses (idempotent). (C3)
      applyChange({ eventType: "UPDATE", new: data });
      // Take On = also open the request straight away, scoped to this task's SO.
      if (onViewRequest) onViewRequest(task.request_id, task.ref_no, task.so_number);
    }
  }

  async function release(task) {
    var updated = Object.assign({}, task, {
      assigned_to: null,
      assigned_to_id: null,
      assigned_at: null,
    });
    var { data, error } = await updateTaskByIdReturning(task.id, {
      assigned_to: null,
      assigned_to_id: null,
      assigned_at: null,
    });
    if (!error) applyChange({ eventType: "UPDATE", new: data || updated });
  }

  function fmtDate(iso) {
    if (!iso) return "-";
    return fmtDateTimeLocal(iso) || "-";
  }

  var stateList = Array.from(
    new Set(
      tasks
        .map(function (t) {
          return t.state;
        })
        .filter(Boolean)
    )
  ).sort();
  var staffList = Array.from(
    new Set(
      tasks
        .map(function (t) {
          return t.assigned_to;
        })
        .filter(Boolean)
    )
  ).sort();
  var shelterList = Array.from(
    new Set(
      tasks
        .map(function (t) {
          return t.shelter_name;
        })
        .filter(Boolean)
    )
  ).sort();

  // Tasks linked to a DRAFT request are never shown. This exclusion used to live
  // inside `filtered`, so the table hid those rows but every stat card counted the
  // raw `tasks` array and reported numbers the list couldn't account for. Hoisted
  // here so the cards and the table agree. Finding B16 item 5, 18 Aug 2026.
  const visibleTasks = tasks.filter(function (t) {
    if (
      t.ref_no &&
      (t.ref_no.startsWith("DRAFT") ||
        (function () {
          var linked = requests?.find(function (r) {
            return r.id === t.request_id || r.refNo === t.ref_no;
          });
          return (
            linked &&
            (linked.status === REQUEST_STATUSES.DRAFT || linked.refNo?.startsWith("DRAFT"))
          );
        })())
    )
      return false;
    return true;
  });

  const filtered = visibleTasks.filter(function (t) {
    if (filterType !== "all" && t.type !== filterType) return false;
    if (filterState !== "all" && t.state !== filterState) return false;
    if (filterShelter !== "all" && t.shelter_name !== filterShelter) return false;
    if (filterStaff !== "all" && t.assigned_to !== filterStaff) return false;
    // "unactioned"/"actioned" are the task-queue's not-done/done filters; they key
    // off the completed_at timestamp column (which is intentionally NOT renamed).
    if (filterStatus === "unactioned" && t.completed_at) return false;
    if (filterStatus === "actioned" && !t.completed_at) return false;
    if (filterStatus === "assigned" && (!t.assigned_to || t.completed_at)) return false;
    if (filterStatus === "mine" && (t.assigned_to !== user.shelterName || t.completed_at))
      return false;
    if (filterStatus === "unassigned" && (t.assigned_to || t.completed_at)) return false;
    if (search.trim()) {
      var q = search.toLowerCase();
      if (
        !(t.ref_no || "").toLowerCase().includes(q) &&
        !(t.shelter_name || "").toLowerCase().includes(q) &&
        !(t.description || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  // Paginate the filtered task rows (25/page); reset to page 1 when the result
  // size changes so a paged view can't strand on an out-of-range page.
  React.useEffect(() => setPage(0), [filtered.length]);
  const pickerPageCount = Math.ceil(filtered.length / PICKER_PAGE_SIZE);
  const pickerSafePage = Math.min(page, Math.max(0, pickerPageCount - 1));
  const pickerPageRows = filtered.slice(
    pickerSafePage * PICKER_PAGE_SIZE,
    (pickerSafePage + 1) * PICKER_PAGE_SIZE
  );
  // Rows scroll inside a viewport-fit box (sticky column headers); filters + headers
  // stay put. Measured once the table renders.
  const listScrollRef = React.useRef(null);
  const listMaxH = useFitViewportHeight(listScrollRef, {
    enabled: !loading && filtered.length > 0,
  });

  // All five counts run over visibleTasks (draft-linked excluded), NOT the raw tasks
  // array — otherwise the cards count rows the table deliberately hides.
  const unactioned = visibleTasks.filter(function (t) {
    return !t.completed_at;
  }).length;
  const unassigned = visibleTasks.filter(function (t) {
    return !t.assigned_to && !t.completed_at;
  }).length;
  // Assigned All = taken on by any picker, not yet actioned. Assigned ME = assigned
  // to the current picker (robust id-or-name check, mirrors the row's isMine); a
  // subset of Assigned All. Unassigned + Assigned All = Unactioned.
  const assigned = visibleTasks.filter(function (t) {
    return !!t.assigned_to && !t.completed_at;
  }).length;
  const assignedMe = visibleTasks.filter(function (t) {
    return (
      !t.completed_at &&
      ((t.assigned_to_id && t.assigned_to_id === user.id) || t.assigned_to === user.shelterName)
    );
  }).length;
  const actionedCount = visibleTasks.filter(function (t) {
    return !!t.completed_at;
  }).length;

  var selSt = "py-[6px] px-[10px] rounded-[6px] border border-[#ccc] text-[13px] bg-white";
  var thSt =
    "px-[10px] py-2 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#607D79] border-b-2 border-[#E0E0E0] whitespace-nowrap sticky top-0 z-[1] bg-[#FAFAFA]";
  var tdSt = "px-[10px] py-[9px] text-[13px] border-b border-[#F0F0F0] align-middle";

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: "#1A2E2B", fontWeight: 700 }}>
            Pickers Task List
          </h1>
          <div style={{ fontSize: 13, color: "#607D79", marginTop: 2 }}>
            Devices and Stationery Pack fulfilment queue
          </div>
        </div>
        <button
          onClick={function () {
            refresh();
          }}
          style={{
            background: "none",
            border: `1px solid ${"#CFE0DE"}`,
            borderRadius: 7,
            padding: "7px 16px",
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "inherit",
            color: "#1A2E2B",
          }}
        >
          ↺ Refresh
        </button>
      </div>

      {/* Stats */}
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 16, marginBottom: 20 }}
      >
        {[
          { label: "Unassigned", value: unassigned, colour: "#E53935" },
          { label: "Assigned ME", value: assignedMe, colour: "#5E35B1" },
          { label: "Assigned All", value: assigned, colour: "#1E88E5" },
          { label: "Unactioned", value: unactioned, colour: "#FFB300" },
          { label: "Actioned", value: actionedCount, colour: "#00897B" },
        ].map(function (s) {
          return (
            <div
              key={s.label}
              style={{
                background: "white",
                borderRadius: 10,
                padding: "10px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
              >
                <div style={{ fontSize: 36, fontWeight: 700, color: s.colour }}>{s.value}</div>
                <div style={{ fontSize: 20, color: "#607D79" }}>{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          placeholder="Search ref, DFV Service, description..."
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
          }}
          className={selSt}
          style={{ width: 240 }}
        />
        <select
          value={filterStatus}
          onChange={function (e) {
            setFilterStatus(e.target.value);
          }}
          className={selSt}
        >
          <option value="all">All statuses</option>
          <option value="unactioned">Unactioned</option>
          <option value="unassigned">Unassigned</option>
          <option value="assigned">Assigned</option>
          <option value="mine">Mine</option>
          <option value="actioned">Actioned</option>
        </select>
        <select
          value={filterType}
          onChange={function (e) {
            setFilterType(e.target.value);
          }}
          className={selSt}
        >
          <option value="all">All types</option>
          <option value="device">Device</option>
          <option value="stationery_qld">Stationery Pack</option>
          <option value="top_up">Top-up</option>
        </select>
        <select
          value={filterState}
          onChange={function (e) {
            setFilterState(e.target.value);
          }}
          className={selSt}
        >
          <option value="all">All states</option>
          {stateList.map(function (s) {
            return (
              <option key={s} value={s}>
                {s}
              </option>
            );
          })}
        </select>
        <select
          value={filterShelter}
          onChange={function (e) {
            setFilterShelter(e.target.value);
          }}
          className={selSt}
        >
          <option value="all">All DFV Services</option>
          {shelterList.map(function (s) {
            return (
              <option key={s} value={s}>
                {s}
              </option>
            );
          })}
        </select>
        <select
          value={filterStaff}
          onChange={function (e) {
            setFilterStaff(e.target.value);
          }}
          className={selSt}
        >
          <option value="all">All staff</option>
          {staffList.map(function (s) {
            return (
              <option key={s} value={s}>
                {s}
              </option>
            );
          })}
        </select>
        <span style={{ fontSize: 12, color: "#607D79", marginLeft: "auto" }}>
          {filtered.length} task{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>Loading tasks...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>
            No tasks match your filters.
          </div>
        ) : (
          <div
            ref={listScrollRef}
            style={
              listMaxH
                ? { overflowX: "auto", overflowY: "auto", maxHeight: listMaxH }
                : { overflowX: "auto" }
            }
          >
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "#F5F7F6" }}>
                  <th className={thSt} style={{ width: "9%" }}>
                    Ref
                  </th>
                  <th className={thSt} style={{ width: "15%" }}>
                    DFV Service /Code
                  </th>
                  <th className={thSt} style={{ width: "9%" }}>
                    Type
                  </th>
                  <th className={thSt} style={{ width: "11%" }}>
                    Created
                  </th>
                  <th className={thSt} style={{ width: "13%" }}>
                    Assigned To
                  </th>
                  <th className={thSt} style={{ width: "11%" }}>
                    Actioned
                  </th>
                  <th className={thSt} style={{ width: "15%" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pickerPageRows.map(function (task, _idx) {
                  var isCompleted = !!task.completed_at;
                  var isMine =
                    (task.assigned_to_id && task.assigned_to_id === user.id) ||
                    task.assigned_to === user.shelterName;
                  var rowBg = isCompleted ? "#FAFAFA" : isMine ? "#F0FAF8" : "#FFFFFF";
                  return (
                    <tr key={task.id} style={{ background: rowBg }}>
                      <td className={tdSt}>
                        {task.ref_no && onViewRequest ? (
                          <RequestRefLink
                            href={
                              task.so_number
                                ? `/requests/${task.request_id}?picker_so=${encodeURIComponent(task.so_number)}`
                                : `/requests/${task.request_id}`
                            }
                            refNo={task.ref_no}
                            onOpen={() =>
                              onViewRequest(task.request_id, task.ref_no, task.so_number)
                            }
                            style={{
                              color: "#00897B",
                              fontWeight: 700,
                              cursor: "pointer",
                              fontSize: 13,
                              textDecoration: "underline",
                            }}
                          />
                        ) : (
                          <span style={{ fontWeight: 700, color: "#00695C", fontSize: 12 }}>
                            {task.ref_no || "-"}
                          </span>
                        )}
                        {task.description && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#607D79",
                              marginTop: 2,
                              whiteSpace: "pre-line",
                            }}
                          >
                            {task.description}
                          </div>
                        )}
                      </td>
                      <td className={tdSt}>
                        <span style={{ fontWeight: 600 }}>{task.shelter_name || "-"}</span>
                        {task.shelter_code && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#00695C",
                              fontWeight: 600,
                              marginTop: 2,
                            }}
                          >
                            {task.shelter_code}
                          </div>
                        )}
                      </td>
                      <td className={tdSt}>
                        <PickerTaskBadge type={task.type} />
                      </td>
                      <td className={tdSt}>{fmtDate(task.created_at)}</td>
                      <td className={tdSt}>
                        {task.assigned_to ? (
                          <span>
                            <span
                              style={{
                                fontWeight: isMine ? 700 : 400,
                                color: isMine ? "#00695C" : "inherit",
                                display: "block",
                              }}
                            >
                              {task.assigned_to}
                              {isMine ? " (you)" : ""}
                            </span>
                            {task.assigned_at && (
                              <span style={{ fontSize: 11, color: "#607D79" }}>
                                {fmtDate(task.assigned_at)}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: "#607D79", fontStyle: "italic" }}>Unassigned</span>
                        )}
                      </td>
                      <td className={tdSt}>{fmtDate(task.completed_at)}</td>
                      <td className={`${tdSt} whitespace-nowrap`}>
                        <div
                          style={{
                            display: "flex",
                            gap: 5,
                            alignItems: "center",
                            flexWrap: "nowrap",
                          }}
                        >
                          {isCompleted ? null : (
                            <div style={{ display: "flex", gap: 6 }}>
                              {/* Assign to Me: claim an UNASSIGNED task; a supervisor
                                    may also take over one held by another picker. A peer
                                    cannot take over another picker's task. */}
                              {!isMine && (isSupervisor || !task.assigned_to) && (
                                <button
                                  onClick={function () {
                                    takeOn(task);
                                  }}
                                  style={{
                                    background: "#00897B",
                                    color: "#FFFFFF",
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "4px 10px",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    fontWeight: 600,
                                  }}
                                >
                                  Assign to Me
                                </button>
                              )}
                              {/* Release: the owner, or a supervisor releasing anyone's. */}
                              {(isMine || (isSupervisor && task.assigned_to)) && (
                                <button
                                  onClick={function () {
                                    release(task);
                                  }}
                                  style={{
                                    background: "none",
                                    border: `1px solid ${"#CFE0DE"}`,
                                    borderRadius: 6,
                                    padding: "4px 10px",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    color: "#607D79",
                                  }}
                                >
                                  Release
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <PageNav page={pickerSafePage} pageCount={pickerPageCount} onPage={setPage} />
        )}
      </div>
    </div>
  );
}

export { PickersPage };
